/**
 * Командная палитра (Ctrl+K). Спецификация — docs/04 §6.10.
 *
 * Клавиатура — первый класс ввода (§1.5): палитра даёт доступ к переходу по
 * номеру задачи, поиску и командам, не требуя мыши. Собрана на Radix Dialog,
 * без сторонней библиотеки палитр — поведение здесь своё.
 */

import * as React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { Search } from "lucide-react";

import { cn } from "@/shared/lib/cn";
import { normalize } from "@/shared/ui/Combobox";
import { Kbd } from "@/shared/ui/Feedback";

export interface Command {
  id: string;
  title: string;
  /** Раздел: «Переходы», «Создание», «Вид». */
  group: string;
  hint?: string;
  shortcut?: readonly string[];
  icon?: React.ReactNode;
  run: () => void;
}

export interface CommandPaletteProps {
  commands: readonly Command[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ввод пользователя: по нему приложение добавляет команды — найденные задачи. */
  onQueryChange?: (query: string) => void;
  /** Команды, которые уже отфильтрованы приложением: их фильтр не трогает. */
  dynamic?: readonly Command[];
}

export function CommandPalette({
  commands,
  open,
  onOpenChange,
  onQueryChange,
  dynamic = [],
}: CommandPaletteProps): React.ReactElement {
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const filtered = React.useMemo(() => {
    if (!query.trim()) {
      return [...dynamic, ...commands];
    }
    const needle = normalize(query);
    return [
      // Динамические команды приходят уже найденными сервером —
      // фильтровать их ещё раз по подстроке значит терять половину находок
      ...dynamic,
      ...commands.filter(
        (command) =>
          normalize(command.title).includes(needle) ||
          normalize(command.group).includes(needle) ||
          normalize(command.hint ?? "").includes(needle),
      ),
    ];
  }, [commands, dynamic, query]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  React.useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(activeIndex);
    }
  }

  function run(index: number): void {
    const command = filtered[index];
    if (!command) {
      return;
    }
    onOpenChange(false);
    command.run();
  }

  const groups = React.useMemo(() => {
    const result = new Map<string, { command: Command; index: number }[]>();
    filtered.forEach((command, index) => {
      const list = result.get(command.group) ?? [];
      list.push({ command, index });
      result.set(command.group, list);
    });
    return result;
  }, [filtered]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[var(--pt-z-overlay)] bg-[var(--pt-overlay-scrim)]" />
        <RadixDialog.Content
          aria-label="Командная палитра"
          className={cn(
            "fixed left-1/2 top-[15vh] z-[var(--pt-z-palette)] w-[560px] max-w-[calc(100vw-32px)]",
            "-translate-x-1/2 overflow-hidden rounded-[var(--pt-radius-8)]",
            "border border-[var(--pt-border-default)] bg-[var(--pt-bg-raised)]",
            "shadow-[var(--pt-shadow-3)] focus:outline-none",
          )}
        >
          <RadixDialog.Title className="sr-only">Командная палитра</RadixDialog.Title>

          <div className="flex items-center gap-[var(--pt-space-8)] border-b border-[var(--pt-border-subtle)] px-[var(--pt-space-12)]">
            <Search
              aria-hidden
              className="size-[var(--pt-icon-lg)] text-[var(--pt-text-tertiary)]"
            />
            <input
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                onQueryChange?.(event.target.value);
              }}
              onKeyDown={onKeyDown}
              placeholder="Команда, задача по номеру или поиск"
              aria-label="Команда, задача по номеру или поиск"
              className="h-[var(--pt-control-h-lg)] w-full bg-transparent text-[var(--pt-fs-md)] text-[var(--pt-text-primary)] placeholder:text-[var(--pt-text-tertiary)] focus:outline-none"
            />
            <Kbd keys={["Esc"]} />
          </div>

          <div
            role="listbox"
            aria-label="Команды"
            className="max-h-[360px] overflow-auto p-[var(--pt-space-4)]"
          >
            {filtered.length === 0 ? (
              <p className="px-[var(--pt-space-12)] py-[var(--pt-space-16)] text-[var(--pt-fs-sm)] text-[var(--pt-text-tertiary)]">
                Ничего не найдено. Попробуйте номер задачи или название раздела.
              </p>
            ) : (
              [...groups.entries()].map(([group, entries]) => (
                <div key={group}>
                  <p className="px-[var(--pt-space-8)] pb-[var(--pt-space-2)] pt-[var(--pt-space-8)] text-[var(--pt-fs-micro)] uppercase tracking-[var(--pt-ls-micro)] text-[var(--pt-text-tertiary)]">
                    {group}
                  </p>
                  {entries.map(({ command, index }) => (
                    <div
                      key={command.id}
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => run(index)}
                      className={cn(
                        "flex h-[var(--pt-row-h)] cursor-default select-none items-center gap-[var(--pt-space-8)]",
                        "rounded-[var(--pt-radius-3)] px-[var(--pt-space-8)] text-[var(--pt-font-ui)]",
                        "text-[var(--pt-text-primary)]",
                        index === activeIndex && "bg-[var(--pt-bg-selected)]",
                      )}
                    >
                      {command.icon && (
                        <span className="text-[var(--pt-text-tertiary)]">{command.icon}</span>
                      )}
                      <span className="flex-1 truncate">{command.title}</span>
                      {command.hint && (
                        <span className="truncate text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
                          {command.hint}
                        </span>
                      )}
                      {command.shortcut && <Kbd keys={command.shortcut} />}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Ctrl+K / Cmd+K открывает палитру из любого места приложения. */
export function useCommandPalette(): { open: boolean; setOpen: (open: boolean) => void } {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { open, setOpen };
}
